import type { ChatMessage } from "./chatApi";
import { connectChatStream, type ChatStreamConnection } from "./chatSocket";
import { ingestEvent, pageContext } from "./analyticsEvents";
import { renderAssistantMarkdown } from "./renderMarkdown";

export type WidgetConfig = {
  brandColor: string;
  greeting: string;
  companyName: string;
  chips: string[];
  position: "bottom-right" | "bottom-left";
  badgeVisible: boolean;
  avatarIcon?: string;
};

export type ProactiveConfig = {
  enabled: boolean;
  message: string;
  initialDelayMs: number;
  followUpDelayMs: number;
  maxPromptsPerSession: number;
  autoDismissMs: number;
  suppressOnMobile: boolean;
};

export type FullWidgetConfig = {
  config: WidgetConfig;
  proactive: ProactiveConfig;
};

export type WidgetController = {
  openPanel: () => void;
  isOpen: () => boolean;
  showCallout: (
    type: "intro" | "resume",
    message: string,
    autoDismissMs: number,
    callbacks?: {
      onClick?: () => void;
      onDismiss?: () => void;
      onExpire?: () => void;
    },
  ) => void;
  hideCallout: () => void;
  sendPageChange: (newPageUrl: string, prevPageUrl?: string) => void;
};

const defaults: WidgetConfig = {
  brandColor: "#1D4ED8",
  greeting: "Hi! How can we help?",
  companyName: "Vera",
  chips: [],
  position: "bottom-right",
  badgeVisible: false,
};

const proactiveDefaults: ProactiveConfig = {
  enabled: true,
  message: "",
  initialDelayMs: 4000,
  followUpDelayMs: 45000,
  maxPromptsPerSession: 2,
  autoDismissMs: 15000,
  suppressOnMobile: false,
};

const ASSISTANT_SUBTITLE = "You can ask me anything";

function msgId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto)
    return crypto.randomUUID();
  return `m_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

function normalizePath(p: string): string {
  if (!p || p === "/") return "/";
  let s = p.startsWith("/") ? p : `/${p}`;
  if (s.length > 1 && s.endsWith("/")) s = s.slice(0, -1);
  return s;
}

function pathMatchesRule(pathname: string, rulePath: string): boolean {
  const p = normalizePath(pathname);
  const r = normalizePath(rulePath);
  if (r === "/") return p === "/";
  return p === r || p.startsWith(`${r}/`);
}

function pickBestRule<T extends Record<string, unknown>>(
  rules: T[],
  pathname: string,
  getUrlPath: (r: T) => string | undefined,
): T | null {
  let best: { rule: T; len: number } | null = null;
  for (const rule of rules) {
    const urlPath = getUrlPath(rule);
    if (typeof urlPath !== "string") continue;
    if (!pathMatchesRule(pathname, urlPath)) continue;
    const len = normalizePath(urlPath).length;
    if (!best || len > best.len) best = { rule, len };
  }
  return best?.rule ?? null;
}

function getWidgetSection(o: Record<string, unknown>): Record<string, unknown> {
  if (o.widget && typeof o.widget === "object")
    return o.widget as Record<string, unknown>;
  if (o.widgetConfig && typeof o.widgetConfig === "object")
    return o.widgetConfig as Record<string, unknown>;
  return o;
}

function resolveGreeting(
  o: Record<string, unknown>,
  pathname: string,
  widget: Record<string, unknown>,
): string {
  const g = o.greetings;
  if (g && typeof g === "object" && !Array.isArray(g)) {
    const gr = g as Record<string, unknown>;
    const defaultG =
      typeof gr.default === "string" ? gr.default : defaults.greeting;
    const rules = Array.isArray(gr.rules) ? gr.rules : [];
    const typed = rules.filter(
      (r): r is Record<string, unknown> => r !== null && typeof r === "object",
    );
    const best = pickBestRule(typed, pathname, (r) =>
      typeof r.urlPath === "string" ? r.urlPath : undefined,
    );
    if (best && typeof best.greeting === "string") return best.greeting;
    return defaultG;
  }
  const leg = widget.greeting;
  if (typeof leg === "string") return leg;
  return defaults.greeting;
}

function resolveChips(
  o: Record<string, unknown>,
  pathname: string,
  widget: Record<string, unknown>,
): string[] {
  const c = o.chips;
  if (c && typeof c === "object" && !Array.isArray(c)) {
    const cr = c as Record<string, unknown>;
    const defaultChips = Array.isArray(cr.default)
      ? cr.default.filter((x): x is string => typeof x === "string")
      : [];
    const rules = Array.isArray(cr.rules) ? cr.rules : [];
    const typed = rules.filter(
      (r): r is Record<string, unknown> => r !== null && typeof r === "object",
    );
    const best = pickBestRule(typed, pathname, (r) =>
      typeof r.urlPath === "string" ? r.urlPath : undefined,
    );
    if (best && Array.isArray(best.chips)) {
      const picked = best.chips.filter(
        (x): x is string => typeof x === "string",
      );
      if (picked.length) return picked;
    }
    return defaultChips;
  }
  if (Array.isArray(c)) {
    return c.filter((x): x is string => typeof x === "string");
  }
  const leg = widget.chips;
  if (Array.isArray(leg)) {
    return leg.filter((x): x is string => typeof x === "string");
  }
  return [];
}

function normalizeConfig(raw: unknown, pathname = "/"): WidgetConfig {
  if (!raw || typeof raw !== "object") return defaults;
  const o = raw as Record<string, unknown>;
  const widget = getWidgetSection(o);
  const src = { ...o, ...widget };
  const pos = src.position === "bottom-left" ? "bottom-left" : "bottom-right";
  return {
    brandColor:
      typeof src.brandColor === "string" ? src.brandColor : defaults.brandColor,
    greeting: resolveGreeting(o, pathname, widget),
    companyName:
      typeof src.companyName === "string" && src.companyName.trim()
        ? src.companyName.trim()
        : defaults.companyName,
    chips: resolveChips(o, pathname, widget),
    position: pos,
    badgeVisible: Boolean(src.badgeVisible),
    avatarIcon: typeof src.avatarIcon === "string" ? src.avatarIcon : undefined,
  };
}

function extractProactiveConfig(raw: unknown): ProactiveConfig {
  if (!raw || typeof raw !== "object") return proactiveDefaults;
  const o = raw as Record<string, unknown>;
  const p = o.proactive;
  if (!p || typeof p !== "object" || Array.isArray(p)) return proactiveDefaults;
  const pc = p as Record<string, unknown>;
  return {
    enabled: typeof pc.enabled === "boolean" ? pc.enabled : proactiveDefaults.enabled,
    message: typeof pc.message === "string" ? pc.message : proactiveDefaults.message,
    initialDelayMs: typeof pc.initialDelayMs === "number" ? pc.initialDelayMs : proactiveDefaults.initialDelayMs,
    followUpDelayMs: typeof pc.followUpDelayMs === "number" ? pc.followUpDelayMs : proactiveDefaults.followUpDelayMs,
    maxPromptsPerSession: typeof pc.maxPromptsPerSession === "number" ? pc.maxPromptsPerSession : proactiveDefaults.maxPromptsPerSession,
    autoDismissMs: typeof pc.autoDismissMs === "number" ? pc.autoDismissMs : proactiveDefaults.autoDismissMs,
    suppressOnMobile: typeof pc.suppressOnMobile === "boolean" ? pc.suppressOnMobile : proactiveDefaults.suppressOnMobile,
  };
}

function trimApiBase(url: string): string {
  return url.replace(/\/+$/, "") || url;
}

export function resolveApiBase(script: HTMLScriptElement): string {
  const attr = script.getAttribute("data-api-base-url");
  if (attr?.trim()) return trimApiBase(attr.trim());
  return trimApiBase(__WIDGET_DEFAULT_API_BASE__);
}

export function findWidgetScript(): HTMLScriptElement | null {
  const cur = document.currentScript;
  if (
    cur &&
    cur instanceof HTMLScriptElement &&
    cur.hasAttribute("data-site-id")
  ) {
    return cur;
  }
  const nodes = document.querySelectorAll<HTMLScriptElement>(
    "script[data-site-id][src]",
  );
  return nodes.length ? nodes[nodes.length - 1]! : null;
}

export async function fetchWidgetConfig(
  apiBase: string,
  siteId: string,
): Promise<FullWidgetConfig> {
  const url = `${trimApiBase(apiBase)}/widget/${encodeURIComponent(siteId)}/config`;
  const res = await fetch(url, { credentials: "omit", mode: "cors" });
  if (!res.ok) throw new Error(`Config request failed (${res.status})`);
  const json: unknown = await res.json();
  const pathname =
    typeof window !== "undefined" &&
    typeof window.location?.pathname === "string"
      ? window.location.pathname
      : "/";
  return {
    config: normalizeConfig(json, pathname),
    proactive: extractProactiveConfig(json),
  };
}

export type MountWidgetOptions = {
  config: WidgetConfig;
  proactive: ProactiveConfig;
  siteId: string;
  apiBase: string;
  sessionToken: string;
  /** Analytics session id for `POST …/widget/events` (not the WebSocket token). */
  sessionId: string | null;
  websocketUrl: string;
  history: ChatMessage[];
};

function hexToRgb(hex: string): string {
  const m = /^#?([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})/i.exec(hex.trim());
  if (!m) return "29, 78, 216";
  return `${parseInt(m[1]!, 16)}, ${parseInt(m[2]!, 16)}, ${parseInt(m[3]!, 16)}`;
}

function injectStyles(root: ShadowRoot): void {
  const style = document.createElement("style");
  style.textContent = `
    :host { all: initial; font-family: system-ui, -apple-system, Segoe UI, sans-serif; }
    .root { position: fixed; z-index: 2147483000; font-size: 14px; line-height: 1.45; }
    .root.br { bottom: 16px; right: 16px; }
    .root.bl { bottom: 16px; left: 16px; }
    .wrap { display: flex; flex-direction: column; align-items: flex-end; gap: 10px; }
    .root.bl .wrap { align-items: flex-start; }
    .panel {
      display: none; flex-direction: column; width: min(360px, calc(100vw - 32px)); max-height: min(520px, 80vh);
      border-radius: 20px; box-shadow: 0 16px 48px rgba(15,23,42,0.22); overflow: hidden;
      background: #fff; color: #0f172a;
    }
    .panel.open { display: flex; }
    .panel.expanded { width: min(420px, calc(100vw - 24px)); max-height: min(85vh, 720px); }
    .head {
      display: flex; align-items: flex-start; justify-content: space-between; gap: 12px;
      padding: 14px 14px 12px; color: #fff; flex-shrink: 0;
    }
    .head-main { display: flex; gap: 10px; min-width: 0; flex: 1; }
    .avatar {
      width: 40px; height: 40px; border-radius: 999px; flex-shrink: 0;
      background: rgba(255,255,255,0.22); display: flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 16px; color: #fff;
    }
    .head-text { min-width: 0; }
    .head-title { font-weight: 700; font-size: 15px; }
    .head-sub { margin-top: 4px; font-size: 12px; font-weight: 500; opacity: 0.95; display: flex; align-items: center; gap: 6px; }
    .live-dot { width: 7px; height: 7px; border-radius: 999px; background: #22c55e; }
    .head-actions { display: flex; gap: 4px; flex-shrink: 0; }
    .icon-btn {
      width: 34px; height: 34px; border: none; border-radius: 8px; cursor: pointer;
      background: rgba(255,255,255,0.15); color: #fff; display: flex; align-items: center; justify-content: center;
      padding: 0;
    }
    .icon-btn:hover { background: rgba(255,255,255,0.22); }
    .transcript {
      flex: 1; min-height: 0; overflow-y: auto; padding: 12px 14px 8px;
      background: #fff;
    }
    .msg-row { display: flex; margin-bottom: 12px; }
    .msg-row.assistant { justify-content: flex-start; gap: 8px; align-items: flex-end; }
    .msg-row.user { justify-content: flex-end; }
    .msg-avatar-sm {
      width: 28px; height: 28px; border-radius: 999px; flex-shrink: 0;
      display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 12px; color: #fff;
    }
    .bubble-assistant {
      max-width: 85%; background: #f1f5f9; color: #1e293b; border-radius: 14px 14px 14px 4px;
      padding: 10px 12px; font-size: 13px; white-space: normal; word-break: break-word;
    }
    .bubble-assistant p { margin: 0 0 0.65em; line-height: 1.5; }
    .bubble-assistant p:last-child { margin-bottom: 0; }
    .bubble-assistant ul, .bubble-assistant ol { margin: 0.35em 0 0.65em; padding-left: 1.25em; }
    .bubble-assistant li { margin: 0.2em 0; line-height: 1.45; }
    .bubble-assistant strong { font-weight: 600; }
    .bubble-assistant a { color: #1d4ed8; text-decoration: underline; }
    .bubble-assistant code { font-size: 0.92em; background: rgba(15,23,42,0.06); padding: 0.1em 0.35em; border-radius: 4px; }
    .typing-bubble { display: inline-flex; align-items: center; gap: 5px; padding: 2px 0; min-height: 1.25em; }
    .typing-bubble span {
      width: 7px; height: 7px; border-radius: 999px; background: #64748b;
      animation: vlq-typing 1.15s ease-in-out infinite;
    }
    .typing-bubble span:nth-child(2) { animation-delay: 0.14s; }
    .typing-bubble span:nth-child(3) { animation-delay: 0.28s; }
    @keyframes vlq-typing {
      0%, 70%, 100% { opacity: 0.35; transform: translateY(0); }
      35% { opacity: 1; transform: translateY(-3px); }
    }
    .bubble-user {
      max-width: 85%; background: #e0e7ff; color: #1e1b4b; border-radius: 14px 14px 4px 14px;
      padding: 10px 12px; font-size: 13px; white-space: pre-wrap; word-break: break-word;
    }
    .chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 8px; }
    .chip {
      border: 1px solid #e2e8f0; background: #fff; border-radius: 999px;
      padding: 6px 12px; font-size: 12px; cursor: pointer; color: #334155;
    }
    .chip:hover { background: #f8fafc; border-color: #cbd5e1; }
    .footer { border-top: 1px solid #e2e8f0; background: #fff; flex-shrink: 0; }
    .input-row { display: flex; align-items: center; gap: 8px; padding: 10px 12px; }
    .input-row input {
      flex: 1; border: none; outline: none; font-size: 14px; background: transparent; color: #0f172a;
    }
    .input-row input::placeholder { color: #94a3b8; }
    .send-btn {
      width: 38px; height: 38px; border-radius: 999px; border: none; cursor: pointer;
      background: #e2e8f0; color: #475569; display: flex; align-items: center; justify-content: center;
      flex-shrink: 0;
    }
    .send-btn:hover:not(:disabled) { background: #cbd5e1; }
    .send-btn:disabled { opacity: 0.5; cursor: not-allowed; }
    .powered {
      text-align: center; font-size: 11px; padding: 0 12px 10px; color: #94a3b8;
    }
    .powered .brand { font-weight: 600; }
    .launcher {
      position: relative;
      width: 56px; height: 56px; border-radius: 999px; border: none; cursor: pointer;
      box-shadow: 0 8px 24px rgba(15,23,42,0.2); display: flex; align-items: center; justify-content: center;
      color: #fff;
    }
    @keyframes vq-pulse {
      0%   { box-shadow: 0 8px 24px rgba(15,23,42,0.2), 0 0 0 0 rgba(var(--vq-brand-rgb), 0.55); }
      70%  { box-shadow: 0 8px 24px rgba(15,23,42,0.2), 0 0 0 14px rgba(var(--vq-brand-rgb), 0); }
      100% { box-shadow: 0 8px 24px rgba(15,23,42,0.2), 0 0 0 0 rgba(var(--vq-brand-rgb), 0); }
    }
    .launcher.vq-pulsing { animation: vq-pulse 2s ease-out infinite; }
    .launcher-badge {
      position: absolute; top: 1px; right: 1px;
      width: 14px; height: 14px; border-radius: 50%;
      background: #ef4444; border: 2px solid #fff;
      pointer-events: none;
    }
    .err-banner {
      margin: 0 12px 8px; padding: 8px 10px; border-radius: 8px; background: #fef2f2; color: #b91c1c; font-size: 12px;
    }
    /* ── Proactive callout ── */
    .callout { display: none; flex-direction: column; align-items: flex-end; max-width: min(280px, calc(100vw - 32px)); }
    .callout.show { display: flex; }
    .root.bl .callout { align-items: flex-start; }
    .callout-body {
      background: #fff; border-radius: 12px; box-shadow: 0 4px 20px rgba(15,23,42,0.14);
      padding: 12px 14px 12px 12px; display: flex; align-items: flex-start; gap: 8px;
      cursor: pointer; border: 1px solid rgba(15,23,42,0.08);
      animation: vq-callout-in 0.25s ease;
    }
    .callout-body:hover { box-shadow: 0 6px 24px rgba(15,23,42,0.2); }
    .callout-msg { flex: 1; font-size: 13px; line-height: 1.5; color: #1e293b; }
    .callout-dismiss {
      width: 20px; height: 20px; border: none; background: none; cursor: pointer;
      color: #94a3b8; font-size: 16px; line-height: 1; padding: 0; flex-shrink: 0;
      border-radius: 4px; display: flex; align-items: center; justify-content: center;
    }
    .callout-dismiss:hover { background: #f1f5f9; color: #475569; }
    /* Arrow triangle pointing down toward the launcher (launcher center = 28px from edge, arrow half-width = 8px → margin = 20px) */
    .callout-arrow {
      width: 0; height: 0;
      border-left: 8px solid transparent; border-right: 8px solid transparent; border-top: 8px solid #fff;
      align-self: flex-end; margin-right: 20px;
      filter: drop-shadow(0 2px 1px rgba(15,23,42,0.06));
    }
    .root.bl .callout-arrow { align-self: flex-start; margin-right: 0; margin-left: 20px; }
    @keyframes vq-callout-in {
      from { opacity: 0; transform: translateY(6px); }
      to { opacity: 1; transform: translateY(0); }
    }
    @keyframes vq-callout-out {
      from { opacity: 1; transform: translateY(0); }
      to   { opacity: 0; transform: translateY(4px); }
    }
    .callout.vq-expiring .callout-body { animation: vq-callout-out 0.8s ease forwards; }
  `;
  root.appendChild(style);
}

function svgLauncher(): string {
  return `<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
    <circle cx="9" cy="10" r="1" fill="currentColor" stroke="none"/>
    <circle cx="12" cy="10" r="1" fill="currentColor" stroke="none"/>
    <circle cx="15" cy="10" r="1" fill="currentColor" stroke="none"/>
  </svg>`;
}

function svgSend(): string {
  return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
    <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>`;
}

function svgExpand(): string {
  return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <polyline points="15 3 21 3 21 9"/><polyline points="9 21 3 21 3 15"/>
    <line x1="21" y1="3" x2="14" y2="10"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`;
}

function svgCollapse(): string {
  return `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
    <polyline points="4 14 10 14 10 20"/><polyline points="20 10 14 10 14 4"/>
    <line x1="14" y1="10" x2="21" y2="3"/><line x1="3" y1="21" x2="10" y2="14"/></svg>`;
}

function assistantLoadingHtml(): string {
  return `<span class="typing-bubble" aria-label="Assistant is typing"><span></span><span></span><span></span></span>`;
}

function initialMessages(
  config: WidgetConfig,
  history: ChatMessage[],
): ChatMessage[] {
  if (history.length > 0) return history.map((m) => ({ ...m }));
  if (config.greeting.trim()) {
    return [
      { id: msgId(), role: "assistant", content: config.greeting.trim() },
    ];
  }
  return [];
}

export function mountWidget(opts: MountWidgetOptions): WidgetController {
  const { config, websocketUrl, sessionId, siteId, apiBase } = opts;
  const messages: ChatMessage[] = initialMessages(config, opts.history);

  const VQ_OPEN_KEY = `vq_open_${siteId}`;

  const host = document.createElement("div");
  host.id = "veroliq-widget-host";
  const shadow = host.attachShadow({ mode: "open" });
  injectStyles(shadow);

  const posClass = config.position === "bottom-left" ? "bl" : "br";
  const root = document.createElement("div");
  root.className = `root ${posClass}`;

  const wrap = document.createElement("div");
  wrap.className = "wrap";

  const panel = document.createElement("div");
  panel.className = "panel";
  panel.setAttribute("role", "dialog");
  panel.setAttribute("aria-label", config.companyName);

  const head = document.createElement("div");
  head.className = "head";
  head.style.background = config.brandColor;

  const initial = (
    config.companyName.trim().charAt(0) ||
    config.companyName ||
    "?"
  ).toUpperCase();
  head.innerHTML = `
    <div class="head-main">
      <div class="avatar" part="avatar">${initial}</div>
      <div class="head-text">
        <div class="head-title">${escapeHtml(config.companyName || "Vera AI")}</div>
        <div class="head-sub"><span class="live-dot"></span><span>${escapeHtml(ASSISTANT_SUBTITLE)}</span></div>
      </div>
    </div>
    <div class="head-actions">
      <button type="button" class="icon-btn" data-action="expand" aria-label="Expand">${svgExpand()}</button>
      <button type="button" class="icon-btn" data-action="close" aria-label="Close chat">×</button>
    </div>
  `;

  const transcript = document.createElement("div");
  transcript.className = "transcript";

  const chipsRow = document.createElement("div");
  chipsRow.className = "chips";
  if (config.chips.length) {
    for (const label of config.chips) {
      const b = document.createElement("button");
      b.type = "button";
      b.className = "chip";
      b.textContent = label;
      chipsRow.appendChild(b);
    }
  }

  const footer = document.createElement("div");
  footer.className = "footer";
  footer.innerHTML = `
    <div class="input-row">
      <input type="text" aria-label="Message" placeholder="Type a message..." autocomplete="off" />
      <button type="button" class="send-btn" aria-label="Send">${svgSend()}</button>
    </div>
    <div class="powered">
      Powered by <span class="brand" part="brand"></span>
    </div>
  `;
  const brandEl = footer.querySelector(".brand") as HTMLSpanElement;
  brandEl.textContent = "Veroliq";
  brandEl.style.color = config.brandColor;

  const input = footer.querySelector<HTMLInputElement>('input[type="text"]')!;
  const sendBtn = footer.querySelector<HTMLButtonElement>(".send-btn")!;

  const errBanner = document.createElement("div");
  errBanner.className = "err-banner";
  errBanner.style.display = "none";

  // ── Callout bubble ──
  const callout = document.createElement("div");
  callout.className = "callout";

  const calloutBody = document.createElement("div");
  calloutBody.className = "callout-body";

  const calloutMsg = document.createElement("span");
  calloutMsg.className = "callout-msg";

  const calloutDismiss = document.createElement("button");
  calloutDismiss.type = "button";
  calloutDismiss.className = "callout-dismiss";
  calloutDismiss.setAttribute("aria-label", "Dismiss");
  calloutDismiss.textContent = "×";

  const calloutArrow = document.createElement("div");
  calloutArrow.className = "callout-arrow";

  calloutBody.appendChild(calloutMsg);
  calloutBody.appendChild(calloutDismiss);
  callout.appendChild(calloutBody);
  callout.appendChild(calloutArrow);

  let calloutTimer: ReturnType<typeof setTimeout> | null = null;
  let calloutExpiryTimer: ReturnType<typeof setTimeout> | null = null;
  let calloutCallbacks: {
    onClick?: () => void;
    onDismiss?: () => void;
    onExpire?: () => void;
  } = {};

  function hideCallout(): void {
    if (calloutTimer) { clearTimeout(calloutTimer); calloutTimer = null; }
    if (calloutExpiryTimer) { clearTimeout(calloutExpiryTimer); calloutExpiryTimer = null; }
    callout.classList.remove("show", "vq-expiring");
    setTimeout(() => { callout.style.display = "none"; }, 260);
    calloutCallbacks = {};
    if (!open) launcher.classList.add("vq-pulsing");
  }

  function showCallout(
    _type: "intro" | "resume",
    message: string,
    autoDismissMs: number,
    callbacks: { onClick?: () => void; onDismiss?: () => void; onExpire?: () => void } = {},
  ): void {
    if (calloutTimer) { clearTimeout(calloutTimer); calloutTimer = null; }
    if (calloutExpiryTimer) { clearTimeout(calloutExpiryTimer); calloutExpiryTimer = null; }
    calloutMsg.textContent = message;
    calloutCallbacks = callbacks;
    callout.classList.remove("show", "vq-expiring");
    callout.style.display = "flex";
    void callout.offsetHeight; // force reflow for animation
    callout.classList.add("show");
    launcher.classList.remove("vq-pulsing");
    if (autoDismissMs > 0) {
      const fadeDelay = Math.max(0, autoDismissMs - 800);
      calloutExpiryTimer = setTimeout(() => {
        callout.classList.add("vq-expiring");
      }, fadeDelay);
      calloutTimer = setTimeout(() => {
        hideCallout();
        callbacks.onExpire?.();
      }, autoDismissMs);
    }
  }

  calloutDismiss.addEventListener("click", (e) => {
    e.stopPropagation();
    const cb = calloutCallbacks;
    hideCallout();
    cb.onDismiss?.();
  });

  calloutBody.addEventListener("click", (e) => {
    e.stopPropagation();
    const cb = calloutCallbacks;
    hideCallout();
    setOpen(true);
    cb.onClick?.();
  });

  let stream: ChatStreamConnection | null = null;
  let streamConnectionGen = 0;
  let chatStartSent = messages.some((m) => m.role === "user");
  let pendingSend: string | null = null;
  let streaming = false;
  let currentAssistantId: string | null = null;

  function findRowByMsgId(id: string): HTMLElement | null {
    for (const el of transcript.querySelectorAll("[data-msg-id]")) {
      const row = el as HTMLElement;
      if (row.dataset.msgId === id) return row;
    }
    return null;
  }

  function updateBubble(el: HTMLElement, text: string) {
    const bubble = el.querySelector(
      ".bubble-assistant, .bubble-user",
    ) as HTMLElement | null;
    if (!bubble) return;
    if (bubble.classList.contains("bubble-assistant")) {
      if (!text.trim()) {
        if (el.dataset.awaitingReply === "1") {
          bubble.innerHTML = assistantLoadingHtml();
        } else {
          bubble.innerHTML = "";
        }
        return;
      }
      el.removeAttribute("data-awaiting-reply");
      bubble.innerHTML = renderAssistantMarkdown(text);
    } else {
      bubble.textContent = text;
    }
  }

  function appendMessageRow(
    m: ChatMessage,
    opts?: { loading?: boolean },
  ): HTMLElement {
    const row = document.createElement("div");
    row.className = `msg-row ${m.role}`;
    row.dataset.msgId = m.id;
    if (m.role === "assistant") {
      const av = document.createElement("div");
      av.className = "msg-avatar-sm";
      av.style.background = config.brandColor;
      av.textContent = initial;
      const bubble = document.createElement("div");
      bubble.className = "bubble bubble-assistant";
      if (opts?.loading) {
        bubble.innerHTML = assistantLoadingHtml();
        row.dataset.awaitingReply = "1";
      } else {
        bubble.innerHTML = renderAssistantMarkdown(m.content);
      }
      row.appendChild(av);
      row.appendChild(bubble);
    } else {
      const bubble = document.createElement("div");
      bubble.className = "bubble bubble-user";
      bubble.textContent = m.content;
      row.appendChild(bubble);
    }
    transcript.appendChild(row);
    transcript.scrollTop = transcript.scrollHeight;
    return row;
  }

  function renderMessages() {
    transcript.innerHTML = "";
    for (const m of messages) appendMessageRow(m);
  }

  function setInputEnabled(on: boolean) {
    input.disabled = !on;
    sendBtn.disabled = !on;
  }

  function showError(text: string) {
    errBanner.textContent = text;
    errBanner.style.display = "block";
  }

  function clearError() {
    errBanner.style.display = "none";
  }

  function ensureSocket(): void {
    if (stream && stream.getReadyState() === WebSocket.OPEN) return;
    if (stream && stream.getReadyState() === WebSocket.CONNECTING) return;
    const gen = ++streamConnectionGen;
    stream = connectChatStream(websocketUrl, {
      onOpen: () => {
        clearError();
        if (pendingSend && stream) {
          stream.sendUserMessage(pendingSend);
          pendingSend = null;
        }
      },
      onClose: () => {
        if (gen === streamConnectionGen) stream = null;
      },
      onToken: (delta) => {
        if (!currentAssistantId) return;
        const m = messages.find((x) => x.id === currentAssistantId);
        if (!m) return;
        m.content += delta;
        const row = findRowByMsgId(currentAssistantId);
        if (row) updateBubble(row, m.content);
        transcript.scrollTop = transcript.scrollHeight;
      },
      onDone: () => {
        const aid = currentAssistantId;
        streaming = false;
        currentAssistantId = null;
        setInputEnabled(true);
        if (aid) {
          const m = messages.find((x) => x.id === aid);
          const row = findRowByMsgId(aid);
          if (m && !m.content.trim() && row) {
            row.removeAttribute("data-awaiting-reply");
            const bubble = row.querySelector(".bubble-assistant");
            if (bubble) bubble.innerHTML = "";
          }
        }
      },
      onError: (msg) => {
        const aid = currentAssistantId;
        streaming = false;
        currentAssistantId = null;
        setInputEnabled(true);
        pendingSend = null;
        showError(msg);
        stream = null;
        if (aid) {
          const row = findRowByMsgId(aid);
          if (row) {
            row.removeAttribute("data-awaiting-reply");
            const bubble = row.querySelector(".bubble-assistant");
            if (bubble)
              bubble.textContent = "Couldn't get a response. Try again.";
          }
        }
      },
    });
  }

  function sendText(text: string) {
    const trimmed = text.trim();
    if (!trimmed || streaming) return;
    clearError();

    if (sessionId && !chatStartSent) {
      chatStartSent = true;
      ingestEvent(apiBase, siteId, sessionId, {
        type: "CHAT_START",
        ...pageContext(),
      });
    }

    const userMsg: ChatMessage = {
      id: msgId(),
      role: "user",
      content: trimmed,
    };
    messages.push(userMsg);
    appendMessageRow(userMsg);

    const asst: ChatMessage = { id: msgId(), role: "assistant", content: "" };
    messages.push(asst);
    currentAssistantId = asst.id;
    streaming = true;
    setInputEnabled(false);
    appendMessageRow(asst, { loading: true });

    pendingSend = trimmed;
    ensureSocket();
    if (stream && stream.getReadyState() === WebSocket.OPEN) {
      stream.sendUserMessage(trimmed);
      pendingSend = null;
    }
  }

  renderMessages();

  panel.appendChild(head);
  panel.appendChild(errBanner);
  panel.appendChild(transcript);
  if (config.chips.length) panel.appendChild(chipsRow);
  panel.appendChild(footer);

  const launcher = document.createElement("button");
  launcher.type = "button";
  launcher.className = "launcher vq-pulsing";
  launcher.style.background = config.brandColor;
  launcher.style.setProperty("--vq-brand-rgb", hexToRgb(config.brandColor));
  launcher.setAttribute("aria-label", "Open chat");
  launcher.innerHTML = svgLauncher();

  if (config.badgeVisible) {
    const badge = document.createElement("span");
    badge.className = "launcher-badge";
    launcher.appendChild(badge);
  }

  let open = false;
  let expanded = false;

  function setOpen(v: boolean) {
    open = v;
    panel.classList.toggle("open", open);
    launcher.classList.toggle("vq-pulsing", !open);
    if (open) {
      launcher.querySelector(".launcher-badge")?.remove();
    }
    try {
      if (open) sessionStorage.setItem(VQ_OPEN_KEY, "1");
      else sessionStorage.removeItem(VQ_OPEN_KEY);
    } catch {
      /* ignore storage errors */
    }
    if (open) {
      hideCallout();
      ensureSocket();
      transcript.scrollTop = transcript.scrollHeight;
      if (sessionId) {
        ingestEvent(apiBase, siteId, sessionId, {
          type: "WIDGET_OPEN",
          ...pageContext(),
        });
      }
    } else if (sessionId) {
      ingestEvent(apiBase, siteId, sessionId, {
        type: "WIDGET_CLOSE",
        ...pageContext(),
      });
    }
  }

  launcher.addEventListener("click", (e) => {
    e.stopPropagation();
    setOpen(!open);
  });

  head
    .querySelector('[data-action="close"]')
    ?.addEventListener("click", (e) => {
      e.stopPropagation();
      setOpen(false);
    });

  head
    .querySelector('[data-action="expand"]')
    ?.addEventListener("click", (e) => {
      e.stopPropagation();
      expanded = !expanded;
      panel.classList.toggle("expanded", expanded);
      const btn = head.querySelector(
        '[data-action="expand"]',
      ) as HTMLButtonElement;
      btn.innerHTML = expanded ? svgCollapse() : svgExpand();
      btn.setAttribute("aria-label", expanded ? "Shrink" : "Expand");
    });

  sendBtn.addEventListener("click", (e) => {
    e.stopPropagation();
    sendText(input.value);
    input.value = "";
  });

  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendText(input.value);
      input.value = "";
    }
  });

  for (const b of chipsRow.querySelectorAll("button.chip")) {
    b.addEventListener("click", (e) => {
      e.stopPropagation();
      const label = (e.currentTarget as HTMLButtonElement).textContent!.trim();
      sendText(label);
    });
  }

  // callout lives between panel and launcher in the flex column
  wrap.appendChild(panel);
  wrap.appendChild(callout);
  wrap.appendChild(launcher);
  root.appendChild(wrap);
  shadow.appendChild(root);
  document.body.appendChild(host);

  // Restore open state from previous page (survives SPA navigation and full reloads in same tab)
  const wasOpen = (() => {
    try { return sessionStorage.getItem(VQ_OPEN_KEY) === "1"; } catch { return false; }
  })();
  if (wasOpen) setOpen(true);

  return {
    openPanel: () => setOpen(true),
    isOpen: () => open,
    showCallout,
    hideCallout,
    sendPageChange: (newPageUrl: string, prevPageUrl?: string) => {
      if (stream && stream.getReadyState() === WebSocket.OPEN) {
        stream.sendPageChange(newPageUrl, prevPageUrl);
      }
    },
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
